import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, FadeIn } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { IMAGES } from "../lib/brand";
import { api, apiError } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const track = (name) => { api.post(`/events/${name}`).catch(() => {}); };

const INSIDE = [
  { k: "Exterior & Grounds", v: "Securing furniture, loose items, doors and openings." },
  { k: "Power & Generators", v: "Fuel, testing and back-up power readiness." },
  { k: "Vendors & Contacts", v: "The key people to reach, before and after." },
  { k: "Household Supplies", v: "Water, provisions and essential inventory." },
  { k: "Documents & Records", v: "What to protect and keep accessible." },
  { k: "Post-Storm Planning", v: "Assessing, restoring and returning to normal." },
];

export default function Checklist() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    document.title = "Hurricane Preparedness Checklist — A Complimentary Resource · Marella Bolognesi";
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.setAttribute("content", "A complimentary hurricane preparedness checklist for Florida homes, drawn from professional Estate Management. A practical resource from Marella Bolognesi.");
    track("checklist_view");
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/checklist/request", form);
      const url = `${BACKEND}/api/checklist/download?token=${data.token}`;
      setDownloadUrl(url);
      window.open(url, "_blank");
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };

  if (done) return (
    <div data-testid="checklist-success" className="pt-20 min-h-screen">
      <section className="mx-auto max-w-[1000px] px-6 lg:px-12 py-24 lg:py-32 text-center">
        <FadeIn>
          <div className="mx-auto w-14 h-14 border border-ink flex items-center justify-center"><Check size={26} /></div>
          <h1 className="mt-8 font-serif font-light text-5xl lg:text-7xl text-ink tracking-tight">It's on its way.</h1>
          <p className="mt-6 text-lg font-light text-ink-soft">Your Hurricane Preparedness Checklist has been sent — and is downloading now.</p>
          <a href={downloadUrl} data-testid="success-redownload" className="mt-4 inline-block text-[11px] uppercase tracking-[0.2em] text-ink link-underline">Download again</a>
        </FadeIn>
        <FadeIn delay={0.12}>
          <div className="mt-16 border-t border-line pt-16 max-w-2xl mx-auto">
            <p className="text-[11px] uppercase tracking-ultra text-accent">While you're here…</p>
            <p className="mt-6 font-serif font-light text-2xl lg:text-3xl text-ink leading-[1.35]">
              Estate Management extends far beyond preparing a home for a storm. Il Salotto Digitale is where I share courses, tools and resources created from the work behind private homes.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/salotto-digitale" onClick={() => track("cta_salotto")} data-testid="success-salotto" className="group bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors flex items-center gap-3">Explore Il Salotto Digitale <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></Link>
              <Link to="/services" onClick={() => track("cta_estate")} data-testid="success-estate" className="border border-ink text-ink px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-ink hover:text-bg transition-colors">Discover Estate Management</Link>
            </div>
          </div>
        </FadeIn>
      </section>
    </div>
  );

  return (
    <div data-testid="checklist-page" className="pt-20">
      {/* Hero */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <FadeIn><MetaLabel>Complimentary Resource · Hurricane Preparedness Checklist</MetaLabel></FadeIn>
          <FadeIn delay={0.08}><h1 className="mt-8 font-serif font-light text-ink text-5xl sm:text-6xl lg:text-[5rem] leading-[1.0] tracking-tight">Prepared before the storm.</h1></FadeIn>
          <FadeIn delay={0.14}><p className="mt-8 text-lg font-light text-ink-soft leading-relaxed max-w-xl">A practical checklist for preparing a residence before severe weather — based on the systems and considerations used in professional Estate Management.</p></FadeIn>
          <FadeIn delay={0.2}>
            <a href="#capture" onClick={(e) => { e.preventDefault(); document.getElementById("capture")?.scrollIntoView({ behavior: "smooth" }); }} data-testid="hero-download" className="group mt-10 inline-flex items-center gap-3 bg-ink text-bg px-8 py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-[#33312E] transition-colors">Get the Checklist <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></a>
          </FadeIn>
        </div>
        <FadeIn delay={0.2} className="lg:col-span-5">
          <div className="overflow-hidden aspect-[4/5]"><img src={IMAGES.waterfrontVilla} alt="A coastal residence prepared for severe weather" className="w-full h-full object-cover" /></div>
        </FadeIn>
      </section>

      {/* Why this matters */}
      <section className="border-t border-line bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 lg:gap-20">
          <Reveal className="lg:col-span-4"><MetaLabel>Why this matters</MetaLabel></Reveal>
          <div className="lg:col-span-8 max-w-2xl space-y-6 text-lg font-light text-ink-soft leading-relaxed">
            <Reveal><p className="font-serif text-2xl lg:text-3xl text-ink leading-[1.3]">In Estate Management, preparedness is part of the work that happens long before a problem arrives.</p></Reveal>
            <Reveal delay={0.08}><p>From exterior furniture and generators to vendors, household supplies, documentation and post-storm planning, preparing a residence requires a system. This checklist provides a practical starting point.</p></Reveal>
          </div>
        </div>
      </section>

      {/* What's inside */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28">
        <Reveal><MetaLabel className="mb-12">What's inside</MetaLabel></Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-2 border-t border-line pt-8">
          {INSIDE.map((it, i) => (
            <Reveal key={it.k} delay={(i % 2) * 0.05}>
              <div className="flex items-start gap-5 py-5 border-b border-line/70">
                <span className="font-serif text-accent text-lg mt-1">{String(i + 1).padStart(2, "0")}</span>
                <div><p className="font-serif text-xl text-ink">{it.k}</p><p className="mt-1 text-sm font-light text-ink-soft">{it.v}</p></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Email capture */}
      <section id="capture" className="border-t border-line bg-ink text-bg">
        <div className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-ultra text-accent">Receive the Checklist</p>
            <h2 className="mt-6 font-serif font-light text-4xl lg:text-5xl leading-tight tracking-tight">Sent straight to your inbox.</h2>
          </div>
          <form onSubmit={submit} data-testid="checklist-form" className="w-full max-w-md lg:justify-self-end space-y-6">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">First Name</label>
              <input data-testid="checklist-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="Your first name" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-bg/60">Email Address</label>
              <input data-testid="checklist-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full bg-transparent border-b border-bg/30 focus:border-bg outline-none py-3 text-bg text-lg font-light transition-colors placeholder:text-bg/40" placeholder="you@email.com" />
            </div>
            <p className="text-xs font-light text-bg/50 leading-relaxed">By downloading this resource you'll also receive <span className="text-bg/80">Notes from Il Salotto</span>. You can unsubscribe at any time.</p>
            <button type="submit" disabled={sending} data-testid="checklist-submit" className="group w-full bg-bg text-ink py-4 text-[11px] uppercase tracking-[0.2em] hover:bg-surface transition-colors flex items-center justify-center gap-3 disabled:opacity-60">{sending ? "Sending…" : "Send Me the Checklist"} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></button>
          </form>
        </div>
      </section>
    </div>
  );
}
